import { useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

import { usePlan } from 'src/hooks/use-plan';

import { openChat } from 'src/utils/crisp';

import { useAuthContext } from 'src/auth/hooks';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

function UsageRow ({ label, current, limit }) {
  const unlimited = limit === null || limit === undefined;
  const usedPct = (!unlimited && limit > 0) ? Math.round((current / limit) * 100) : 0;

  const isNearLimit = !unlimited && usedPct >= 80;
  const isAtLimit = !unlimited && usedPct >= 100;

  const barColor = () => {
    if (isAtLimit) return 'error';
    if (isNearLimit) return 'warning';
    return 'primary';
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {label}
        </Typography>

        <Typography variant="caption" sx={{ fontWeight: 700, color: isAtLimit ? 'error.main' : 'text.primary' }}>
          {current.toLocaleString()}
          {!unlimited && (
            <Typography component="span" variant="caption" sx={{ color: 'text.disabled', fontWeight: 400 }}>
              /{limit.toLocaleString()}
            </Typography>
          )}
        </Typography>
      </Stack>

      {!unlimited && (
        <LinearProgress
          variant="determinate"
          value={Math.min(usedPct, 100)}
          color={barColor()}
          sx={{
            height: 4,
            borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          }}
        />
      )}
    </Box>
  );
}

UsageRow.propTypes = {
  label: PropTypes.string.isRequired,
  current: PropTypes.number.isRequired,
  limit: PropTypes.number,
};

// ----------------------------------------------------------------------

export default function NavUpgrade () {
  const { user } = useAuthContext();
  const { usage, refreshUsage } = usePlan();
  const hasLiveChat = true;

  useEffect(() => {
    if (user) {
      refreshUsage();
    }
  }, [user, refreshUsage]);

  return (
    <Stack sx={{ px: 2, pb: 2.5, pt: 0.5 }}>
      {/* Usage panel. The numbers are re-read after anything that changes the seller's listings,
          so it keeps showing the last ones it has while the next read is in flight — hiding the
          panel on every refresh would make it blink in and out as the seller works. */}
      {usage && (
        <Box
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
        >
          <Stack spacing={1.5}>
            <UsageRow
              label="Active Listings"
              current={usage.usage.activeListings}
              limit={usage.limits.activeListings}
            />

            {usage.limits.draftListings != null && (
              <UsageRow
                label="Inactive Listings"
                current={usage.usage.draftListings || 0}
                limit={usage.limits.draftListings}
              />
            )}

            {/* <Divider sx={{ borderStyle: 'dashed', opacity: 0.5 }} /> */}

            <UsageRow
              label="AI Credits"
              current={usage.limits.aiCreditsPerMonth - usage.usage.aiCredits}
              limit={usage.limits.aiCreditsPerMonth}
            />

            {usage.usage.aiCreditsAddOn > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Iconify icon="mdi:plus-circle-outline" width={14} sx={{ color: 'success.main' }} />
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  +{usage.usage.aiCreditsAddOn} add-on credits
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      )}

      {/* Chat button */}
      <Button
        fullWidth
        size="small"
        variant="soft"
        color="primary"
        startIcon={<Iconify icon="solar:chat-round-dots-bold" width={16} />}
        onClick={() => openChat()}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
        }}
      >
        {hasLiveChat ? 'Chat with us' : 'Email us'}
      </Button>
    </Stack>
  );
}
